import React from "react";
import OpenSeadragon from "openseadragon";
import {makeStyles} from "@mui/styles";
import {alpha} from "@mui/material";
import * as Proptypes from "prop-types";

const useStyles = makeStyles((theme) => ({
    overlay: {
        border: '3px solid #00a6ff',
        borderRadius: '0.2em',
        '&:hover': {
            border: '0px',
            backgroundColor: alpha(theme.palette.text.primary, 0.1),
        },
    },
    button: {
        cursor: "pointer",
        borderRadius: '0.2em',
        '&:hover': {
            padding: "2px",
        },
    },
}));

export const RENDER_MODES = {
    // This mode clip the selected element
    FLUID: "fluid",
    // This mode print the full image with an overlay on the selected element
    FULL: "full",
}

useOpenSeaDragonViewer.propTypes = {
    viewerID: Proptypes.string.isRequired,
    image: Proptypes.string.isRequired,
    config: Proptypes.any
}

export function useOpenSeaDragonViewer(viewerID, image, config) {
    const [viewer, setViewer] = React.useState(null);
    const [nextID, setNextID] = React.useState(1);
    const [latestElem, setLatestElem] = React.useState(null);
    const [mode, setMode] = React.useState(RENDER_MODES.FULL);
    const classes = useStyles();

    React.useEffect(() => {
        refresh();
        // eslint-disable-next-line
    }, [mode]);

    const initViewer = () => {
        viewer && viewer.destroy();
        let option = config ? config : {
            id: viewerID,
            zoomInButton: `${viewerID}-sd-zoom-in`,
            zoomOutButton: `${viewerID}-sd-zoom-out`,
            homeButton: `${viewerID}-sd-home`,
            fullPageButton: `${viewerID}-sd-full-page`,
            preserveViewport: true,
            showNavigator: true,
            navigatorSizeRatio: 0.2,
            tileSources: [{
                tileSource: image,
                x: 0.0,
                y: 0.0,
                width: 1
            }]
        };
        setViewer(OpenSeadragon(option));
        setLatestElem(null);
    }

    function hash(elem) {
        elem.hashID = elem.hashID || ('elem_' + setNextID(nextID + 1));
        return elem.hashID;
    }

    function switchMode(mode) {
        switch (mode) {
            case RENDER_MODES.FLUID:
                setMode(RENDER_MODES.FLUID);
                break;
            default:
                viewer.world.removeAll();
                viewer.addTiledImage({tileSource: image, x: 0, y: 0, width: 1});
                setMode(RENDER_MODES.FULL);
        }
    }

    function refresh() {
        if (viewer) {
            if (latestElem)
                render(latestElem);
            else
                viewer.addTiledImage({tileSource: image, x: 0, y: 0, width: 1});
        }
    }

    function render(elem) {
        setLatestElem(elem);
        if (!viewer)
            return;
        removeAllOverlays();
        switch (mode) {
            case RENDER_MODES.FLUID:
                viewer.world.removeAll();
                fluidMode(elem);
                break;
            default:
                if (viewer.world.getItemCount() > 1) {
                    viewer.world.removeAll();
                    viewer.addTiledImage({tileSource: image, x: 0, y: 0, width: 1});
                }
                setOverlays(elem);
        }
    }

    function fluidMode(elem) {
        const boxes = elem.getAttribute("boundingbox").split(" ");
        const factor = parseFloat(elem.getAttribute("factor"));
        let rect = addClips(boxes, factor);
        setTimeout(function () {
            zoomOnRect(new OpenSeadragon.Rect(0, 0, rect.width, rect.width), 0.2, 0.2);
        }, 200);
        addHTMLOverlay(elem, 0, rect, "div");
        addArrows(elem, rect);
    }

    function addClips(boxes, factor) {
        const growth = 10;
        let maxWidth = 0;
        boxes.forEach((box) => {
            let coords = box.split(",").map(parseFloat);
            if (coords[2] > maxWidth)
                maxWidth = coords[2];
        });
        maxWidth += 2 * growth;
        let totalHeight = 0;
        boxes.forEach((box) => {
            let coords = box.split(",").map(parseFloat);
            coords[0] = coords[0] - growth;
            coords[1] = coords[1] - growth;
            coords[2] = coords[2] + 2 * growth;
            coords[3] = coords[3] + 2 * growth;
            let x = ((maxWidth - coords[2]) / 2 - coords[0]) / factor;
            let y = totalHeight - coords[1] / factor;
            let rect = new OpenSeadragon.Rect(coords[0], coords[1], coords[2], coords[3]);
            viewer.addTiledImage({tileSource: image, clip: rect, x: x, y: y, width: 1});
            totalHeight += coords[3] / factor + 0.002;
        });
        return new OpenSeadragon.Rect(0, 0, maxWidth / factor, totalHeight);
    }

    function addArrows(elem, rect) {
        const type = elem.id.split("-")[0];
        if (elem.previousElementSibling) {
            let elemName = "" + elem.previousElementSibling.id;
            if (elemName.startsWith(type))
                addArrow(elem, "previous", rect);
        }
        if (elem.nextElementSibling) {
            let elemName = "" + elem.nextElementSibling.id;
            if (elemName.startsWith(type))
                addArrow(elem, "next", rect);
        }
    }

    function setOverlays(elem) {
        const rect = addBoxesOverlays(elem);
        setTimeout(zoomOnOverlays, 100);
        addArrows(elem, rect);
    }

    function addBoxesOverlays(elem) {
        const boxes = elem.getAttribute("boundingbox").split(" ");
        let rect = null;
        for (let i = 0; i < boxes.length; i++) {
            rect = addBoxOverlay(elem, boxes[i], i);
        }
        return rect;
    }

    function addBoxOverlay(elem, box, i) {
        const coords = box.split(",").map(parseFloat);
        const factor = parseFloat(elem.getAttribute("factor"));
        const rect = new OpenSeadragon.Rect(coords[0] / factor, coords[1] / factor, coords[2] / factor, coords[3] / factor);
        addHTMLOverlay(elem, i, rect, "div");
        return rect;
    }

    function addArrow(elem, direction, rect) {
        const w = rect.width * 0.07 < 0.010 ? 0.010 : rect.width * 0.07;
        const y = rect.y + rect.height + 0.005;
        let arrow_rect = null;
        let target = null;
        let placement;
        switch (direction) {
            case "previous":
                arrow_rect = new OpenSeadragon.Point(rect.x + rect.width / 2 - w, y);
                target = elem.previousElementSibling;
                placement = OpenSeadragon.OverlayPlacement.TOP_RIGHT;
                break;
            case "next":
                arrow_rect = new OpenSeadragon.Point(rect.x + rect.width / 2 + w, y);
                target = elem.nextElementSibling;
                placement = OpenSeadragon.OverlayPlacement.TOP_LEFT;
                break;
            default:
                break;
        }
        let elt = document.createElement("div");
        elt.id = direction + '-runtime-overlay';
        elt.className += " " + classes.button;
        let img = document.createElement("img")
        img.id = direction + '-arrow-overlay';
        img.src = "/assets/" + direction + ".png";
        img.width = '40';
        img.onclick = function () {
            target.focus()
        };
        elt.appendChild(img);
        viewer.addOverlay({
            element: elt,
            location: arrow_rect,
            checkResize: false,
            placement: placement
        });
        new OpenSeadragon.MouseTracker({
            element: elt.id,
            clickHandler: function (event) {
                // Cancel zoom on click on overlays
            }
        });
    }

    function addHTMLOverlay(elem, index, rect, htmlTag) {
        let div = document.createElement(htmlTag);
        div.id = hash(elem) + "-overlay";
        if (index)
            div.id += "-" + index;
        if (htmlTag === "div")
            div.className += " " + classes.overlay;
        viewer.addOverlay({
            element: div,
            location: rect,
        });
        return div;
    }

    function removeAllOverlays() {
        while (viewer.currentOverlays.length > 0) {
            viewer.removeOverlay(viewer.currentOverlays[0].element.id);
        }
    }

    function zoomOnOverlays() {
        let unionR;
        let r;
        let isInit = false;
        for (let i = 0; i < viewer.currentOverlays.length; i++) {
            r = viewer.currentOverlays[i].bounds;
            if (!isNaN(r.x) && !isNaN(r.y) && !isNaN(r.width) && !isNaN(r.height)) {
                if (isInit) {
                    unionR = unionR.union(r);
                } else {
                    unionR = new OpenSeadragon.Rect(r.x, r.y, r.width, r.height);
                    isInit = true;
                }
            }
        }
        if (isInit) {
            zoomOnRect(unionR, 0.1, 0.1);
        }
    }

    function zoomOnRect(r, dw, dh) {
        let rect = r;
        if (dw > 0 || dh > 0)
            rect = new OpenSeadragon.Rect(r.x - dw / 2, r.y - dh / 2, r.width + dw, r.height + dh);
        viewer.viewport.fitBounds(rect);
    }

    return {
        // OSD instance
        viewer,
        // function to call for init the OSD viewer
        initViewer,
        // function to call to render the selected element
        render,
        // switch render modes
        switchMode
    };
}